import { coreAuthClient } from "../pre/SEC_01_02_coreAuthClient";

/**
 * THE TRANSLATOR
 * Standardizes the BFF "Golden Source" for the UI.
 */
function translateGoldenSource(raw) {
  if (!raw) return { ok: false, message: "No response from server." };

  const success = raw.success || raw.Success || raw.ok;
  const session = raw.session || raw.Session;
  const user = raw.user || raw.User || raw;
  const refresh = raw.refresh || raw.Refresh;
  const message = raw.message || raw.Message;
  const matrix = raw.matrix || raw.Matrix || null;

  const isOk = (success === true);

  // Industrial Logging
  console.group("%c 🛡️ CORE-BFF HANDSHAKE REPORT", "color: #00D1FF; font-weight: bold;");
  console.log("Status:", isOk ? "✅ SUCCESS" : "❌ REJECTED");
  console.log("Matrix Detected:", !!matrix);
  console.log("Payload:", raw);
  console.groupEnd();

  return {
    ok: isOk,
    message: message || (isOk ? "Success" : "There was a problem with your inputs."),
    // USE_ORIGINAL_METHOD (+ method 'google' / 'amazon' / 'email') when the email belongs to another sign-in method
    code: raw.code || raw.Code || null,
    method: raw.method || raw.Method || null,
    user: isOk ? {
      guid: user.siteUserGuid || user.SiteUserGuid,
      email: user.emailAddress || user.EmailAddress || user.email,
      name: user.userName || user.UserName,
      // Social sign-in only: the Google / Amazon account name (session-only)
      displayName: user.displayName || user.DisplayName || null,
      companyId: user.companyId || user.CompanyId || user.CompanyID || null
    } : null,
    session: isOk && session ? {
      sessionId: session.sessionId || session.SessionId,
      expires: session.expiresAtUtc || session.ExpiresAtUtc
    } : null,
    refresh: isOk && refresh ? {
      refreshTokenId: refresh.refreshTokenId || refresh.RefreshTokenId
    } : null,
    matrix: matrix,
    raw,
  };
}

export const authClient = {
  // ---------------------------------------------------------------
  // 1. PUBLIC GETTERS (Consolidated to Session)
  // ---------------------------------------------------------------
  getMatrix() {
    try {
      const stored = sessionStorage.getItem('SEC_USER_MATRIX');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse stored matrix", e);
      return null;
    }
  },

  async getSession() {
    const sessionId = sessionStorage.getItem('SEC_SESSION_ID');
    const userGuid = sessionStorage.getItem('SEC_USER_GUID');

    if (!sessionId) return { ok: false };

    const userEmail = sessionStorage.getItem('SEC_USER_EMAIL');
    return {
      ok: true,
      raw: {
        user: { siteUserGuid: userGuid, emailAddress: userEmail, userName: userEmail },
        session: { sessionId: sessionId }
      }
    };
  },

  // ---------------------------------------------------------------
  // 2. AUTH ACTIONS
  // ---------------------------------------------------------------
  async ping() {
    const raw = await coreAuthClient.ping();
    return translateGoldenSource(raw);
  },

  async signIn({ email, password }) {
    const raw = await coreAuthClient.signIn({ email, password });
    const result = translateGoldenSource(raw);
    if (result.ok) {
      this._commitToStorage(result);
    }
    return result;
  },

  async socialSignIn({ provider, token }) {
    const raw = await coreAuthClient.socialSignIn({ provider, token });
    const result = translateGoldenSource(raw);
    if (result.ok) {
      this._commitToStorage(result);
    }
    return result;
  },

  async login(creds) {
    return await this.signIn(creds);
  },

  async register({ username, email, password }) {
    const raw = await coreAuthClient.register({
      userName: username || email,
      email,
      password,
    });
    return translateGoldenSource(raw);
  },

  async verifyEmailToken(verificationToken) {
    const raw = await coreAuthClient.verifyEmail({ verificationToken });
    return translateGoldenSource(raw);
  },

  async signOut() {
    const sessionId = sessionStorage.getItem('SEC_SESSION_ID');
    if (sessionId) {
      try {
        await coreAuthClient.revoke({ SessionId: sessionId });
      } catch (e) {
        console.warn("Server revocation failed, clearing local storage.");
      }
    }
    this._clearStorage();
    window.location.href = "/auth/sign-in";
  },

  // ---------------------------------------------------------------
  // 3. PASSWORD & GDPR
  // ---------------------------------------------------------------
  async forgotPassword({ email }) {
    const raw = await coreAuthClient.forgotPassword({ email });
    return translateGoldenSource(raw);
  },

  async resetPassword({ token, newPasswordHash }) {
    const payload = {
      token,
      newPasswordHash
    };

    console.log("RESET → payload:", payload);
    const raw = await coreAuthClient.resetPassword(payload);
    console.log("RESET → response:", raw);
    return translateGoldenSource(raw);
  },



  async exportUserData(siteUserGuid) {
    try {
      const response = await fetch('/api/v1/auth/gdpr/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('SEC_SESSION_ID')}`
        },
        body: JSON.stringify({ siteUserGuid })
      });
      if (!response.ok) throw new Error('Export failed');
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GDPR_Export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("GDPR Export Error:", error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // 4. STORAGE (PRIVATE - CONSOLIDATED TO SESSION)
  // ---------------------------------------------------------------
  _commitToStorage(result) {
    if (result.session?.sessionId) {
      sessionStorage.setItem('SEC_SESSION_ID', result.session.sessionId);
    }
    if (result.refresh?.refreshTokenId) {
      sessionStorage.setItem('SEC_REFRESH_ID', result.refresh.refreshTokenId);
    }
    if (result.user?.guid) {
      sessionStorage.setItem('SEC_USER_GUID', result.user.guid);
    }
    if (result.user?.email) {
      sessionStorage.setItem('SEC_USER_EMAIL', result.user.email);
    }
    // Use env-defined Corp GUID for Sportgramme; fall back to server value for Sustina
    const companyId = process.env.REACT_APP_COMPANY_ID || result.user?.companyId;
    if (companyId) {
      sessionStorage.setItem('SEC_USER_COMPANY_ID', companyId);
    }
    if (result.matrix) {
      sessionStorage.setItem('SEC_USER_MATRIX', JSON.stringify(result.matrix));
      console.log("✅ Matrix persisted to SESSION storage.");
    }

    // 🎯 HUSK: Empty placeholders for future local use
    localStorage.removeItem('SEC_SESSION_ID');
    localStorage.removeItem('SEC_USER_GUID');
    localStorage.removeItem('SEC_USER_MATRIX');
  },

  _clearStorage() {
    sessionStorage.removeItem('SEC_SESSION_ID');
    sessionStorage.removeItem('SEC_REFRESH_ID');
    sessionStorage.removeItem('SEC_USER_GUID');
    sessionStorage.removeItem('SEC_USER_EMAIL');
    sessionStorage.removeItem('SEC_USER_MATRIX');
    sessionStorage.removeItem('SEC_USER_COMPANY_ID');

    // Clear the husk
    localStorage.removeItem('SEC_USER_MATRIX');
  }
};